// Graba audio del microfono y lo entrega como un Blob WAV de 16kHz, mono,
// 16-bit PCM -- el formato que exige la API de evaluacion de pronunciacion
// de Azure AI Speech. NO se usa MediaRecorder porque su salida normal
// (audio/webm;codecs=opus) no es un formato que esa API acepte
// directamente; en vez de eso se captura el audio "crudo" con la Web Audio
// API (AudioContext + ScriptProcessorNode) y se arma el archivo WAV a mano.

function flotanteAEntero16(muestras) {
  const buffer = new ArrayBuffer(muestras.length * 2)
  const vista = new DataView(buffer)
  let offset = 0
  for (let i = 0; i < muestras.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, muestras[i]))
    vista.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true)
  }
  return buffer
}

// Los microfonos suelen grabar a 44.1kHz o 48kHz; Azure exige 16kHz, asi
// que hay que reducir la cantidad de muestras antes de enviar el audio.
function remuestrear(muestras, tasaOriginal, tasaDestino) {
  if (tasaDestino === tasaOriginal) return muestras
  const ratio = tasaOriginal / tasaDestino
  const nuevaLongitud = Math.round(muestras.length / ratio)
  const resultado = new Float32Array(nuevaLongitud)
  for (let i = 0; i < nuevaLongitud; i++) {
    resultado[i] = muestras[Math.round(i * ratio)] || 0
  }
  return resultado
}

function crearWav(muestras16kHz) {
  const bytesPorMuestra = 2
  const datosPCM = flotanteAEntero16(muestras16kHz)
  const bufferWav = new ArrayBuffer(44 + datosPCM.byteLength)
  const vista = new DataView(bufferWav)

  function escribirTexto(offset, texto) {
    for (let i = 0; i < texto.length; i++) vista.setUint8(offset + i, texto.charCodeAt(i))
  }

  escribirTexto(0, 'RIFF')
  vista.setUint32(4, 36 + datosPCM.byteLength, true)
  escribirTexto(8, 'WAVE')
  escribirTexto(12, 'fmt ')
  vista.setUint32(16, 16, true)          // tamaño del sub-chunk fmt
  vista.setUint16(20, 1, true)           // PCM sin comprimir
  vista.setUint16(22, 1, true)           // 1 canal (mono)
  vista.setUint32(24, 16000, true)       // sample rate
  vista.setUint32(28, 16000 * bytesPorMuestra, true) // byte rate
  vista.setUint16(32, bytesPorMuestra, true)         // block align
  vista.setUint16(34, 16, true)          // bits por muestra
  escribirTexto(36, 'data')
  vista.setUint32(40, datosPCM.byteLength, true)

  new Uint8Array(bufferWav, 44).set(new Uint8Array(datosPCM))
  return new Blob([bufferWav], { type: 'audio/wav' })
}

export function haySoporteGrabacionPronunciacion() {
  return typeof navigator !== 'undefined' &&
    !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) &&
    !!(window.AudioContext || window.webkitAudioContext)
}

// Duracion maxima que acepta Azure cuando la evaluacion de pronunciacion
// esta activada -- se corta la grabacion sola pasado este tiempo, para no
// quedarse grabando indefinidamente si el estudiante se olvida de parar.
export const DURACION_MAXIMA_MS = 12000

export class GrabadorPronunciacion {
  constructor() {
    this.stream = null
    this.contexto = null
    this.procesador = null
    this.fuente = null
    this.trozos = []
    this.grabando = false
  }

  async iniciar() {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    const AudioContextClase = window.AudioContext || window.webkitAudioContext
    this.contexto = new AudioContextClase()
    this.fuente = this.contexto.createMediaStreamSource(this.stream)
    // 4096 muestras por bloque, 1 canal de entrada y salida -- suficiente
    // para voz y compatible con todos los navegadores modernos.
    this.procesador = this.contexto.createScriptProcessor(4096, 1, 1)
    this.trozos = []
    this.procesador.onaudioprocess = (evento) => {
      const datos = evento.inputBuffer.getChannelData(0)
      this.trozos.push(new Float32Array(datos))
    }
    this.fuente.connect(this.procesador)
    // El procesador necesita estar conectado a un destino para que Chrome
    // dispare los eventos onaudioprocess, aunque no reproducimos nada.
    this.procesador.connect(this.contexto.destination)
    this.grabando = true
  }

  detener() {
    if (!this.grabando) return null
    this.grabando = false
    this.procesador.disconnect()
    this.fuente.disconnect()
    this.stream.getTracks().forEach(t => t.stop())

    const longitudTotal = this.trozos.reduce((s, t) => s + t.length, 0)
    if (longitudTotal === 0) { this.contexto.close(); return null }

    const todasLasMuestras = new Float32Array(longitudTotal)
    let offset = 0
    for (const trozo of this.trozos) {
      todasLasMuestras.set(trozo, offset)
      offset += trozo.length
    }

    const tasaOriginal = this.contexto.sampleRate
    this.contexto.close()
    const muestras16kHz = remuestrear(todasLasMuestras, tasaOriginal, 16000)
    return crearWav(muestras16kHz)
  }

  cancelar() {
    if (!this.grabando) return
    this.grabando = false
    try { this.procesador.disconnect() } catch (e) {}
    try { this.fuente.disconnect() } catch (e) {}
    if (this.stream) this.stream.getTracks().forEach(t => t.stop())
    if (this.contexto) this.contexto.close()
  }
}
